import {
  alpha,
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  styled,
  Typography,
} from '@mui/material';

export const Title = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  margin: '12px 0',
  color: theme.palette.primary.main,
  [theme.breakpoints.down('xl')]: {
    fontSize: '22px',
  },
}));

type RadioBoxProps = {
  selected?: boolean;
};

export const RadioBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'select',
})<RadioBoxProps>(({ theme, selected }) => ({
  border: '1px solid',
  padding: '12px 16px',
  borderRadius: '8px',
  borderColor: selected
    ? theme.palette.primary.light
    : theme.palette.common.grey,
  display: 'flex',
  flexDirection: 'column',
  width: '45%',
  minHeight: '112px',
}));

export const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.common.grey,
  marginLeft: '1.5rem',
  fontSize: '14px',
  [theme.breakpoints.down('xl')]: {
    fontSize: '12px',
  },
  '&.Mui-focused': {
    color: theme.palette.common.grey,
  },
}));

export const StyledBox = styled(Box)(() => ({
  border: `1px solid #E9E9E9`,
  margin: '16px 0 8px',
  padding: '20px 20px 10px 10px',
  borderRadius: '4px',
  backgroundColor: '#F5F5F4',
}));

export const StyledTitleBox = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& span': { color: theme.palette.primary.main },
  [theme.breakpoints.down('xl')]: {
    fontSize: '14px',
  },
}));

export const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
    [theme.breakpoints.down('xl')]: {
      fontSize: '14px',
    },
  },
}));

export const ContainerForBoxApps = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const StyledBoxApp = styled(StyledBox)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px !important',
  width: '100%',
  [theme.breakpoints.down('xl')]: {
    width: '90%',
  },
}));

export const ContainerAppStoreGooglePlay = styled(Box)(() => ({
  display: 'flex',
  gap: '8px',
}));

export const ButtonBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  margin: '48px 0',
  gap: '16px',
}));

export const FilledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  fontWeight: 'bold',
  [theme.breakpoints.down('xl')]: {
    maxWidth: '200px',
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

export const OutlinedStyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  border: '2px solid',
  borderColor: theme.palette.secondary.main,
  color: theme.palette.secondary.main,
  backgroundColor: theme.palette.common.white,
  fontWeight: 'bold',
  [theme.breakpoints.down('xl')]: {
    maxWidth: '200px',
  },
}));

export const StyledRadioGroup = styled(RadioGroup)(() => ({
  gap: '8px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const HowToScanner = styled(Box)(() => ({
  backgroundColor: '#F5F5F4',
  width: '100%',
  padding: '20px',
  borderRadius: '4px',
  borderColor: '#e9e9e9',
}));

export const TitleItem = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& span': {
    color: theme.palette.primary.main,
  },
}));

export const ListItem = styled(Typography)(({ theme }) => ({
  margin: '12px 0 12px 32px',
  fontSize: '16px',
  [theme.breakpoints.down('xl')]: {
    fontSize: '14px',
  },
}));

export const GoogleAuthContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  '& h3': {
    fontSize: '18px',
    [theme.breakpoints.down('xl')]: {
      fontSize: '16px',
    },
  },
  '& svg': {
    height: '128px',
    width: '128px',
    [theme.breakpoints.down('xl')]: {
      height: '112px',
      width: '112px',
    },
  },
}));

export const QRCodeContainer = styled(Box)(() => ({
  background: 'grey',
  padding: '12px',
  marginBottom: '20px',
  borderRadius: '8px',
  '& > div': {
    background: 'white',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
  },
}));

export const SecretBox = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '14px 12px',
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& > span': {
    fontSize: '16px',
  },
}));

export const Span = styled('span')(({ theme }) => ({
  color: theme.palette.common.grey,
  margin: '12px 0 6px 0',
  fontSize: '14px',
  [theme.breakpoints.down('xl')]: {
    fontSize: '12px',
  },
}));

export const NewCodeButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '16px',
  textDecoration: 'underline',
  cursor: 'pointer',
  '&.Mui-disabled': {
    color: alpha(theme.palette.primary.main, 0.5),
    cursor: 'pointer',
  },
  [theme.breakpoints.down('xl')]: {
    fontSize: '14px',
  },
}));

export const Counter = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '8px',
  fontWeight: 'bold',
  '& span': {
    fontWeight: 'normal',
    fontSize: '24px',
    color: theme.palette.primary.main,
  },
}));

export const SectionDialog = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  textAlign: 'center',
  marginBottom: '12px',
  [theme.breakpoints.down('xl')]: {
    fontSize: '14px',
  },
}));

export const CodeButtonBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginTop: '60px',
}));
